import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment/moment';
import { withSnackbar } from 'notistack';
import T from 'i18n-react';
import styled from 'styled-components';
import withStyles from '@material-ui/core/styles/withStyles';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import ReasonTextField from '../../core/Applicants/ReasonTextField';
import { getErrorMessageByStatus } from '../../core/ajax';
import {
  APP_VIEW,
  API_V1_APPLICANT_APPROVE,
  API_V1_APPLICANT_REJECT,
  CANCEL_POLICY,
  ROOMS,
} from '../routing';
import { AppDialog } from '../../core/Dialog/AppDialog';
import { SERVICE_COLOR } from '../../core/styles/variables';
import DialogWithClose from '../../core/Dialog/DialogWithClose';

const ReasonContainer = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing(2)}px;
  margin-bottom: ${({ theme }) => theme.spacing(2)}px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.text.secondary};
`;

const DateLabel = styled.div`
  margin: ${({ theme }) => theme.spacing(0, 2, 0.5, 0)};
  font-weight: bold;
`;

const Date = styled.div`
  font-weight: bold;
  color: ${({ theme }) => theme.palette.primary.dark};
`;

const Description = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)}px;
`;

const ButtonLink = styled.span`
  margin: 0;
  color: ${SERVICE_COLOR};

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
`;

const styles = (theme) => ({
  content: {
    marginBottom: theme.spacing(2),
    color: theme.props.palette.primary.main,
  },
  formCancelPolicy: {
    marginBottom: 0,
  },
  cancelPolicyNote: {
    fontSize: theme.typography.pxToRem(12),
    margin: theme.spacing(0, 0, 2, 4),
  },
  cancelPolicyDialogPaper: {
    width: `calc(100% - ${theme.spacing(1)}px)`,
    height: `calc(100% - ${theme.spacing(1)}px)`,
    margin: theme.spacing(1),
  },
});

class DecisionDialog extends AppDialog {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      isValid: false,
      reason: '',
      isCancelPolicyClicked: false,
      isCancelPolicyChecked: false,
      openCancelPolicyDialog: false,
    };
  }

  isApproved = () => this.props.action === 'approve';

  handleOpen(event) {
    this.setState({ open: true, isValid: true });
  }

  handleClose(event) {
    this.setState({ open: false, isValid: false });
  }

  getPlanTitle = () => {
    const { plan } = this.props;
    return `${plan.slice(0, 30)}${plan.length > 30 ? '...' : ''}`;
  };

  getTitle = () =>
    T.translate(this.isApproved() ? 'confirm.lastCheck' : 'confirm.reject');

  getActionButtonText = () =>
    T.translate(this.isApproved() ? 'confirm.approve' : 'confirm.reject');

  getDialogProps = () => ({ ...this.props.DialogProps, keepMounted: true });
  getActionButtonProps = () => ({
    ...(!this.isApproved() && { color: 'secondary' }),
  });

  handleChangeCancelPolicyCheckbox = (event) => {
    this.setState({
      isCancelPolicyChecked: event.target.checked,
    });
  };

  handleClickCancelPolicy = (e) => {
    this.setState({
      isCancelPolicyClicked: true,
      openCancelPolicyDialog: true,
    });
    e.preventDefault();
  };

  handleCloseCancelPolicyDialog = () => {
    this.setState({ openCancelPolicyDialog: false });
  };

  dialogContentBaseRenderer = () => {
    const { requestedStartDate, requestedEndDate, classes, bizReason } =
      this.props;
    const {
      isCancelPolicyClicked,
      isCancelPolicyChecked,
      openCancelPolicyDialog,
    } = this.state;

    return (
      <>
        {!!bizReason && (
          <ReasonContainer>
            <DateLabel>{T.translate('applicant.approval.bizReason')}</DateLabel>
            {bizReason}
          </ReasonContainer>
        )}
        {requestedStartDate !== null && requestedEndDate !== null && (
          <>
            <DateLabel>{T.translate('applicant.confirms.term')}</DateLabel>
            <Date>
              {moment(requestedStartDate).format(T.translate('moment.date'))}〜
              {moment(requestedEndDate).format(T.translate('moment.date'))}
            </Date>
            <Description>
              {T.translate('applicant.approval.date_change')}
            </Description>
          </>
        )}
        <Typography classes={{ root: classes.content }}>
          {T.translate('applicant.approval.content')}
        </Typography>
        <FormControlLabel
          classes={{ root: classes.formCancelPolicy }}
          control={
            <Checkbox
              disabled={!isCancelPolicyClicked}
              color="primary"
              onChange={this.handleChangeCancelPolicyCheckbox}
              value={isCancelPolicyChecked}
            />
          }
          label={
            <Typography>
              {T.translate('applicant.approval.confirmedX', {
                x: (
                  <ButtonLink href="" onClick={this.handleClickCancelPolicy}>
                    {T.translate('applicant.approval.cancelPolicy')}
                  </ButtonLink>
                ),
              })}
            </Typography>
          }
        />
        <Typography
          classes={{ root: classes.cancelPolicyNote }}
          color="textSecondary"
        >
          {T.translate('applicant.approval.cancelPolicyNote')}
        </Typography>
        <DialogWithClose
          fullWidth
          open={openCancelPolicyDialog}
          onClickClear={this.handleCloseCancelPolicyDialog}
          // Deprecatedなイベントです
          onBackdropClick={this.handleCloseCancelPolicyDialog}
          classes={{ paper: classes.cancelPolicyDialogPaper }}
        >
          <Iframe
            title={T.translate('applicant.approval.cancelPolicy')}
            src={`${APP_VIEW}${CANCEL_POLICY}`}
          />
        </DialogWithClose>
      </>
    );
  };

  dialogContentRenderer = () => {
    if (this.isApproved()) {
      return this.dialogContentBaseRenderer();
    } else {
      const { name } = this.props;
      return (
        <ReasonTextField
          name={name}
          onChange={(event, isValid) =>
            this.setState({ isValid, reason: event.target.value })
          }
          label={T.translate('applicant.reject.reason')}
          helperText={T.translate('applicant.reject.helper')}
          subtitle={T.translate('applicant.reject.subtitle')}
          notification={T.translate('applicant.reject.content')}
          required
        />
      );
    }
  };

  isActionButtonDisabled(): boolean {
    return !this.state.isValid || super.isActionButtonDisabled();
  }

  getActionButtonProps = () => {
    const disabled = this.isApproved()
      ? !this.state.isCancelPolicyChecked
      : !this.state.reason;
    return { disabled };
  };

  handleAccept(event) {
    const { applicantId, successMessage } = this.props;
    const { reason } = this.state;

    this.setState({ progress: true });
    this.fetchData(
      (this.isApproved()
        ? API_V1_APPLICANT_APPROVE
        : API_V1_APPLICANT_REJECT
      ).replace(':applicantId', applicantId),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(!this.isApproved() && { user_reason: reason }),
      },
      () => {
        if (this.isApproved()) {
          global.Turbolinks.visit(ROOMS, { action: 'replace' });
          if (successMessage)
            global.alertAfterTurbolinksLoad(successMessage, 'success');
        } else {
          global.Turbolinks.visit('/', { action: 'replace' });
          global.alertAfterTurbolinksLoad(
            T.translate('applicant.reject.alert', {
              plan: this.getPlanTitle(),
            }),
          );
        }
      },
      (response, data) => {
        // approveとcancelのAPIを条件分岐で処理しているため、以下のフラグが必要
        // 理想は、APIインターフェースとなるロジックは大元で分かれる作りにして、以下のフラグは用いないようにしたい
        const hasNewErrorStructure = data.hasOwnProperty('code');
        const message = hasNewErrorStructure
          ? data.errors.map(({ errorMessage }) => errorMessage)
          : getErrorMessageByStatus(response, data, 'decision-dialog-error');
        this.handleError({ message, severity: 'error' });
      },
    );
  }
}

DecisionDialog.propTypes = {
  ...AppDialog.propTypes,
  plan: PropTypes.string,
  action: PropTypes.oneOf(['approve', 'reject']),
  features: PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.string,
  applicantId: PropTypes.any,
  successMessage: PropTypes.string,
  requested_start_date: PropTypes.string,
  requested_end_date: PropTypes.string,
};

export default withSnackbar(withStyles(styles)(DecisionDialog));
